import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Phone,
  Heart,
  Shield,
  Ear,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

const AboutPage = () => {
  const values = [
    {
      icon: Ear,
      title: "L'écoute active",
      description: "Prendre le temps de comprendre vos préoccupations, vos craintes et vos attentes."
    },
    {
      icon: Heart,
      title: "Le respect",
      description: "Traiter chaque patient avec dignité, sans jugement et avec bienveillance."
    },
    {
      icon: Shield,
      title: "La douceur",
      description: "Créer un environnement apaisant et procéder avec délicatesse à chaque étape."
    },
    {
      icon: Sparkles,
      title: "La précision professionnelle",
      description: "Offrir un travail de qualité supérieure grâce à une expertise ciblée et constamment perfectionnée."
    }
  ];

  return (
    <div className="bg-[#F5F5F0]" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-white section-padding" data-testid="about-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="heading-playfair heading-xl text-slate-900">
                Une approche humaine avant tout
              </h1>
              <p className="text-body-lg">
                Marcela Chiri Valdivia, d.d., offre un accompagnement professionnel et attentionné, 
                dans un environnement calme où la confiance et le respect sont au cœur de chaque intervention.
              </p>
              <p className="text-body">
                Après des années de pratique, elle a développé une approche unique 
                qui place le bien-être du patient au cœur de chaque intervention. 
                Son expertise en prothèses dentaires lui permet 
                d'offrir des résultats optimaux.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="about-hero-cta">
                  <Button className="btn-primary text-base px-8 py-6">
                    Prendre rendez-vous
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="hero-image aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_sourires-precision/artifacts/z035whj1_B2F19532-230D-4264-ADC3-7AAAC8C61443.PNG"
                  alt="Marcela Chiri Valdivia, d.d., denturologiste"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 right-4 md:-bottom-6 md:right-8 bg-slate-900 text-white rounded-xl shadow-lg p-4 md:p-6">
                <p className="heading-playfair text-lg font-semibold">
                  Marcela Chiri Valdivia
                </p>
                <p className="text-slate-300 text-sm">d.d., Denturologiste</p>
                <p className="text-[#D4AF37] text-sm mt-1">
                  Spécialiste en prothèses dentaires
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding" data-testid="about-values">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
              Les valeurs qui guident notre pratique
            </h2>
            <p className="text-body-lg max-w-2xl mx-auto">
              Chaque patient mérite d'être accompagné avec dignité et bienveillance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="card p-8 hover:shadow-lg transition-shadow"
                data-testid={`value-card-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="icon-circle bg-[#F5F5F0] flex-shrink-0">
                    <value.icon className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="heading-playfair text-xl font-semibold mb-3 text-slate-900">
                      {value.title}
                    </h3>
                    <p className="text-body">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="bg-white section-padding" data-testid="about-philosophy">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="hero-image aspect-[4/3] rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_sourires-precision/artifacts/udca4jzh_3C048F0B-08D8-49CC-8143-DE73D82FF076.PNG"
                alt="Salle de traitement de la clinique Sourires Saint-Bruno"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="heading-playfair heading-lg text-slate-900">
                Notre philosophie
              </h2>
              <p className="text-body-lg">
                Nous croyons que chaque personne mérite des soins dentaires 
                de qualité dans un environnement respectueux et apaisant.
              </p>
              <p className="text-body">
                Trop souvent, les patients arrivent avec des expériences négatives 
                qui ont créé de l'anxiété autour des soins dentaires. Notre mission 
                est de changer cette perception en offrant une expérience positive, 
                où chaque patient se sent écouté et en confiance.
              </p>
              <p className="text-body">
                Notre expertise en prothèses dentaires nous permet 
                d'offrir des résultats exceptionnels en termes de confort, 
                de stabilité et d'esthétique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="about-cta">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="heading-playfair text-2xl md:text-3xl text-white mb-6">
              Rencontrons-nous
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Nous serions ravis de vous accueillir pour une première consultation. 
              Venez découvrir notre approche et discuter de vos besoins.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="about-cta-appointment">
                <Button className="btn-call text-base px-8 py-6">
                  Prendre rendez-vous
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a 
                href="tel:450-441-3636" 
                className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-slate-900 text-base px-8 py-6"
                data-testid="about-cta-call"
              >
                <Phone className="w-5 h-5" />
                <span>450-441-3636</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
